// Dotted "sound rings" visual for the realtime voice agent (Figma: Ai Chatbot 21, "sound-dots").

// The period for the constant breathing when the agent is on.
const PULSE_PERIOD_SECONDS = 3;
// How much larger/smaller than "normal" size (as a percentage) the breathing gets.
const PULSE_SIZE_MULTIPLIER = 1.015;
// Seconds for one full rotation of a ring at speed 1 (alternate rings counter-rotate).
const ROTATION_PERIOD_SECONDS = 90;
// Seconds for a volume ripple to travel from the inner ring to the outer ring.
const RIPPLE_PERIOD_SECONDS = 1.4;
// How far (as a fraction of ring radius) rings push out at full volume.
const RIPPLE_SIZE_MULTIPLIER = 0.08;
// Total transition time when deflating (dimming).
const DEFLATE_TRANSITION_TIME_MS = 1000;
// Total transition time when inflating.
const INFLATE_TRANSITION_TIME_MS = 300;
// How many frames to use when smoothing the volume input.
const VOLUME_WINDOW_SIZE = 6;

// Ring geometry as fractions of the canvas half-size (Figma frame is 375px, inner ring r≈48px, outer dots r≈168px).
const INNER_RING_RADIUS = 0.255;
const RING_SPACING = 0.058;
const OUTER_RING_COUNT = 11;
const DOT_RGB = "255, 255, 255";

interface Point {
  x: number;
  y: number;
}

interface Ring {
  radius: number;
  dotSpacing: number;
  dotRadius: number;
  alpha: number;
}

interface Shape {
  generation: number;
  time: number;
  // multiplier that controls the progress of time
  speed: number;
  deflation: number;
  agentNoise: number[];
  userNoise: number[];
  end: boolean;
}

// eslint-disable-next-line import/prefer-default-export
export enum VoiceBotStatus {
  Active = "active",
  Sleeping = "sleeping",
  NotStarted = "not-started",
}

type Context = CanvasRenderingContext2D;

enum Attributes {
  orbState = "orb-state",
  agentVolume = "agent-volume",
  userVolume = "user-volume",
}

const pi = (n: number): number => Math.PI * n;

const lerp = (start: number, stop: number, amt: number): number =>
  amt * (stop - start) + start;

const clamp = (
  { low, high }: { low: number; high: number },
  val: number,
): number => Math.min(high, Math.max(val, low));

/**
 * https://easings.net/#easeInOutQuad
 */
const easeInOutQuad = (x: number): number =>
  x < 0.5 ? 2 * x * x : 1 - (-2 * x + 2) ** 2 / 2;

const MAX_CANVAS_DPR = 2;

const getHiDpiScale = (ctx: Context): number => ctx.getTransform().a;

const getCenter = (ctx: Context): Point => {
  const { width, height } = ctx.canvas.getBoundingClientRect();
  return { x: width / 2, y: height / 2 };
};

const clearCanvas = (ctx: Context): void => {
  const scale = getHiDpiScale(ctx);
  if (scale > 1) {
    const { width, height } = ctx.canvas.getBoundingClientRect();
    if (width > 0 && height > 0) {
      ctx.clearRect(0, 0, width, height);
      return;
    }
  }

  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
};

/** Inner ring is a dense, near-solid circle; outer rings get sparser and fainter. */
const rings: Ring[] = [
  { radius: INNER_RING_RADIUS, dotSpacing: 2.4, dotRadius: 0.7, alpha: 0.5 },
  ...Array.from({ length: OUTER_RING_COUNT }, (_, i): Ring => {
    const step = i + 1;
    const falloff = 1 - step / (OUTER_RING_COUNT + 1);
    return {
      radius: INNER_RING_RADIUS + step * RING_SPACING,
      dotSpacing: 4.5 + step * 0.55,
      dotRadius: lerp(0.65, 0.9, falloff),
      alpha: 0.04 + 0.36 * falloff ** 1.4,
    };
  }),
];

const averageVolume = (noise: number[]): number => {
  const recent = noise.slice(-VOLUME_WINDOW_SIZE);
  return recent.reduce((a, b) => a + b, 0) / recent.length;
};

const breathing = (shape: Shape): number =>
  1 +
  (PULSE_SIZE_MULTIPLIER - 1) *
    Math.sin((shape.time * pi(2)) / PULSE_PERIOD_SECONDS / 1000) *
    lerp(1, 0, shape.deflation);

const drawRing = (
  ctx: Context,
  center: Point,
  radius: number,
  ring: Ring,
  rotation: number,
  alpha: number,
): void => {
  const count = Math.max(12, Math.round((pi(2) * radius) / ring.dotSpacing));
  const step = pi(2) / count;

  ctx.fillStyle = `rgba(${DOT_RGB}, ${clamp({ low: 0, high: 1 }, alpha)})`;
  ctx.beginPath();
  for (let d = 0; d < count; d += 1) {
    const angle = rotation + d * step;
    const x = center.x + radius * Math.cos(angle);
    const y = center.y + radius * Math.sin(angle);
    ctx.moveTo(x + ring.dotRadius, y);
    ctx.arc(x, y, ring.dotRadius, 0, pi(2));
  }
  ctx.fill();
};

const draw = (ctx: Context, shape: Shape, last: number, now: number): void => {
  if (shape.end) return;
  // eslint-disable-next-line no-param-reassign
  shape.time += (now - last) * lerp(1, shape.speed, shape.deflation);
  clearCanvas(ctx);

  const center = getCenter(ctx);
  const maxRadius = Math.min(center.x, center.y);
  const volume = Math.max(averageVolume(shape.agentNoise), averageVolume(shape.userNoise));
  const seconds = shape.time / 1000;
  const presence = lerp(1, 0.45, easeInOutQuad(shape.deflation));
  const scale = breathing(shape) * lerp(1, 0.94, shape.deflation);

  rings.forEach((ring, i) => {
    const ripple =
      Math.max(0, Math.sin(pi(2) * (seconds / RIPPLE_PERIOD_SECONDS - i / rings.length))) * volume;
    const radius = maxRadius * ring.radius * scale * (1 + RIPPLE_SIZE_MULTIPLIER * ripple);
    const direction = i % 2 === 0 ? 1 : -1;
    const rotation = direction * pi(2) * (seconds / ROTATION_PERIOD_SECONDS) + i * 0.37;
    const alpha = (ring.alpha + 0.35 * ripple) * presence;
    drawRing(ctx, center, radius, ring, rotation, alpha);
  });

  requestAnimationFrame((t) => {
    draw(ctx, shape, now, t);
  });
};

// How completely to deflate (dim/shrink the rings).
const deflationDepth = (orbState: string): number => {
  switch (orbState) {
    case VoiceBotStatus.Active:
      return 0;
    case VoiceBotStatus.Sleeping:
      return 0.65;
    case VoiceBotStatus.NotStarted:
      return 1;
    default:
      return 0;
  }
};

// How quickly time moves forward. 1 means "1 second per real second", 0.5 means "1 second per 2 real seconds".
const speedOf = (orbState: string): number => {
  switch (orbState) {
    case VoiceBotStatus.Active:
      return 1;
    case VoiceBotStatus.Sleeping:
      return 0.5;
    case VoiceBotStatus.NotStarted:
      return 0.2;
    default:
      return 1;
  }
};

const transition = (
  generation: number,
  start: { time: number; deflation: number },
  end: { deflation: number },
  shape: Shape,
  now: number = start.time,
) => {
  // drop this transition if a newer one has been produced
  if (shape.generation > generation) return;

  if (end.deflation !== shape.deflation) {
    const transitionTime =
      end.deflation > start.deflation
        ? DEFLATE_TRANSITION_TIME_MS
        : INFLATE_TRANSITION_TIME_MS;
    const progress = easeInOutQuad(
      clamp({ low: 0, high: 1 }, (now - start.time) / transitionTime),
    );

    // eslint-disable-next-line no-param-reassign
    shape.deflation =
      progress === 1
        ? end.deflation
        : lerp(start.deflation, end.deflation, progress);
  }

  if (shape.deflation !== end.deflation) {
    requestAnimationFrame((ts) => {
      transition(generation, start, end, shape, ts);
    });
  }
};

const setupHalCanvasContext = (
  host: HTMLElement,
  canvas: HTMLCanvasElement,
): CanvasRenderingContext2D | null => {
  const cssWidth = Number(host.getAttribute("width")) || 400;
  const cssHeight = Number(host.getAttribute("height")) || 320;
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_CANVAS_DPR);

  canvas.width = Math.max(1, Math.round(cssWidth * dpr));
  canvas.height = Math.max(1, Math.round(cssHeight * dpr));

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }

  if (dpr > 1) {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    // Keep bitmap high-res but display at the same CSS size as before.
    canvas.style.width = "100%";
    canvas.style.height = "auto";
    canvas.style.maxWidth = "100%";
    canvas.style.display = "block";
  }

  return ctx;
};

const noiseBuffer = (fill: number): number[] =>
  Array(VOLUME_WINDOW_SIZE).fill(fill);

customElements.define(
  "openai-hal",
  class extends HTMLElement {
    canvas: HTMLCanvasElement;

    shape: Shape;

    constructor() {
      super();
      this.canvas = document.createElement("canvas");
      this.shape = {
        generation: 0,
        time: 0,
        speed: speedOf(VoiceBotStatus.NotStarted),
        deflation: deflationDepth(VoiceBotStatus.NotStarted),
        agentNoise: noiseBuffer(0),
        userNoise: noiseBuffer(0),
        end: false,
      };
    }

    connectedCallback() {
      const orbState =
        this.getAttribute(Attributes.orbState) || VoiceBotStatus.NotStarted;
      const agentVolume =
        Number(this.getAttribute(Attributes.agentVolume)) || 0;
      const userVolume = Number(this.getAttribute(Attributes.userVolume)) || 0;

      this.appendChild(this.canvas);

      this.shape = {
        generation: 0,
        time: 0,
        speed: speedOf(orbState),
        deflation: deflationDepth(orbState),
        agentNoise: noiseBuffer(agentVolume),
        userNoise: noiseBuffer(userVolume),
        end: false,
      };

      const ctx = setupHalCanvasContext(this, this.canvas);
      if (ctx) {
        const now = performance.now();
        requestAnimationFrame((t) => {
          if (this.shape) draw(ctx, this.shape, now, t);
        });
      }
    }

    disconnectedCallback() {
      this.shape.end = true;
    }

    static get observedAttributes() {
      return Object.values(Attributes);
    }

    attributeChangedCallback(name: string, _: string, newValue: string) {
      switch (name) {
        case Attributes.orbState:
          this.shape.generation += 1;
          this.shape.speed = speedOf(newValue);
          requestAnimationFrame((time) => {
            const start = {
              deflation: this.shape.deflation,
              time,
            };
            const end = {
              deflation: deflationDepth(newValue),
            };
            transition(this.shape.generation, start, end, this.shape);
          });
          break;
        case Attributes.agentVolume:
          if (Number.isNaN(Number(newValue))) break;
          this.shape.agentNoise.shift();
          this.shape.agentNoise.push(Number(newValue));
          break;
        case Attributes.userVolume:
          if (Number.isNaN(Number(newValue))) break;
          this.shape.userNoise.shift();
          this.shape.userNoise.push(Number(newValue));
          break;
        default:
          break;
      }
    }
  },
);
